import { Accordion, AccordionActions, AccordionDetails, AccordionSummary } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import TextField from 'core/components/validatedForm/TextField'
import Button from 'core/elements/button/Button'
import PlusButton from 'core/elements/button/PlusButton'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { routeManager } from 'core/utils/routes'
import VolumeBackendConfigurationCards from './VolumeBackendConfigurationCards'
import AssignedVolumeTypes from './AssignedVolumeTypes'
import { VolumeType } from './utils/volumeTypeMapping'
import Tooltip from 'core/elements/tooltip'
import AddNewVolumeType from './AddNewVolumeType'
import SimpleLink from 'core/components/SimpleLink'

const useStyles = makeStyles<Theme>((theme) => ({
  accordion: {
    border: `1px solid ${theme.components.table.border}`,
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: 0,
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default,
    minHeight: '48px !important',
    height: 48,
    '&.MuiAccordionSummary-root.Mui-expanded': {
      minHeight: '48px !important',
      borderBottom: `1px solid ${theme.components.table.border}`,
    },
    '& .MuiAccordionSummary-content': {
      margin: '0 !important',
      alignItems: 'center',
    },
  },
  accordionSummaryContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
    '&.MuiAccordionDetails-root': {
      padding: theme.spacing(3),
    },
  },
  accordionActions: {
    padding: theme.spacing(2),
    justifyContent: 'flex-start',
    borderTop: `1px solid ${theme.components.table.border}`,
  },
  volumeBackendNameField: {
    width: '350px',
    marginBottom: theme.spacing(3),
    '& .MuiInputBase-root': {
      height: 32,
    },
    '& .MuiInputBase-input': {
      padding: '4px 8px',
      fontSize: '14px',
    },
  },
  sectionContainer: {
    display: 'grid',
    gap: theme.spacing(2),
    width: '100%',
  },
  volumeTypesSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${theme.components.table.border}`,
  },
  volumeTypesInfo: {
    color: theme.palette.grey[600],
  },
}))

export interface VolumeBackendAccordionProps {
  params: any
  updateParams: (params: any) => void
  getParamsUpdater: (key: string) => (value: any) => void
  volumeBackend: any
  idx: number
  usedVolumeBackends: string[]
  uniqueVolumeTypeNameValidator: any
  onDelete: (volumeType: any) => void
  onUpdateName: (name: string, id: string, idx: number) => void
  assignedVolumeTypes: VolumeType[]
  loadingVolumeTypes: boolean
  isExistingVolumeBackend?: boolean
  hasError?: boolean
  setStorageVolumeErrorIndexes?: (indexes: number[]) => void
}

export default function VolumeBackendAccordion({
  params,
  updateParams,
  getParamsUpdater,
  volumeBackend,
  idx,
  usedVolumeBackends,
  uniqueVolumeTypeNameValidator,
  onDelete,
  onUpdateName,
  assignedVolumeTypes,
  loadingVolumeTypes,
  isExistingVolumeBackend,
  hasError,
  setStorageVolumeErrorIndexes,
}: VolumeBackendAccordionProps) {
  const classes = useStyles()
  const history = useHistory()
  const [expanded, setExpanded] = useState(hasError || !volumeBackend.type)
  const [showVolumeTypeModal, setShowVolumeTypeModal] = useState(false)

  // Check if there are any backend configurations for this volume type
  const hasBackendConfigs = useMemo(
    () =>
      params.volumeBackendConfigs?.some((config) => config?.volumeBackendId === volumeBackend.id),
    [params.volumeBackendConfigs, volumeBackend.id],
  )

  const handleAccordionChange = useCallback((_, nextExpanded) => {
    setExpanded(nextExpanded)
    setStorageVolumeErrorIndexes([])
  }, [])

  const navigateToVolumeTypes = useCallback(() => {
    history.push(
      routeManager.routes.openstack.createVolumeType.path({
        volumeBackendName: volumeBackend.type,
      }),
    )
  }, [history])

  useEffect(() => {
    if (hasError) {
      setExpanded(true)
    }
  }, [hasError])

  const filteredConfigs = useMemo(
    () =>
      params.volumeBackendConfigs
        .map((config, idx) => ({ ...config, index: idx }))
        .filter((config) => config.volumeBackendId === volumeBackend.id),
    [params.volumeBackendConfigs, volumeBackend],
  )

  const saveVolumeTypes = useCallback(
    (newVolumeTypes: any) => {
      updateParams({ volumeTypes: [...params?.volumeTypes, newVolumeTypes] })
      setShowVolumeTypeModal(false)
    },
    [params?.volumeTypes],
  )

  return (
    <>
      <Accordion expanded={expanded} onChange={handleAccordionChange} className={classes.accordion}>
        <AccordionSummary
          className={classes.accordionSummary}
          expandIcon={<FontAwesomeIcon size="xs">chevron-down</FontAwesomeIcon>}
        >
          <div className={classes.accordionSummaryContent}>
            <Text component="span" variant="caption1">
              #{idx + 1} Volume Backend {volumeBackend.type ? ` - ${volumeBackend.type}` : ''}
            </Text>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <div>
            <TextField
              id={`volumeBackends.${idx}.type`}
              label="Volume Backend Name"
              onChange={(value) => onUpdateName(value, volumeBackend.id, idx)}
              value={volumeBackend.type || ''}
              placeholder="Name"
              validations={[uniqueVolumeTypeNameValidator]}
              className={classes.volumeBackendNameField}
              required={filteredConfigs.length > 0}
            />
          </div>
          <div className={classes.sectionContainer}>
            <Text variant="caption1">Volume Backend Configurations</Text>
            <VolumeBackendConfigurationCards
              params={params}
              updateParams={updateParams}
              volumeBackend={volumeBackend}
              usedVolumeBackends={usedVolumeBackends}
              filteredConfigs={filteredConfigs}
            />
            <div className={classes.volumeTypesSection}>
              <Text variant="caption1">Volume Types</Text>
              <Text variant="body2" className={classes.volumeTypesInfo}>
                To create a Volume Type and assign it to a Backend, you must provide the{' '}
                <b>Volume Backend Name</b> and configure{' '}
                <b>at least one Volume Backend Configuration</b> before proceeding.
              </Text>

              <AssignedVolumeTypes
                assignedTypes={assignedVolumeTypes}
                loading={loadingVolumeTypes}
                showEmpty
                removeVolumeType={(volumeType) =>
                  getParamsUpdater('volumeTypes')(
                    params.volumeTypes.filter(
                      (v) =>
                        !(
                          v.name === volumeType.name &&
                          v.extra_specs.volume_backend_name === volumeBackend.type
                        ),
                    ),
                  )
                }
              />
              <Tooltip
                message={
                  !isExistingVolumeBackend
                    ? 'You must enter the volume backend name and configure at least one Backend Configuration before you can create a Volume Type'
                    : ''
                }
              >
                <Button
                  variant="secondary"
                  disabled={!hasBackendConfigs}
                  onClick={() => {
                    setShowVolumeTypeModal(true)
                  }}
                  data-testid="create-assign-volume-type-button"
                >
                  Create & Assign Volume Type
                </Button>
              </Tooltip>
            </div>
          </div>
        </AccordionDetails>
        <AccordionActions className={classes.accordionActions}>
          <PlusButton
            disabled={hasBackendConfigs}
            onClick={() => onDelete(volumeBackend)}
            icon="minus-circle"
            label="Delete Volume Backend"
            tooltip={
              hasBackendConfigs
                ? 'You must remove the volume backend configurations for this volume backend before you can delete it'
                : ''
            }
            data-testid="delete-volume-backend-button"
          />
        </AccordionActions>
      </Accordion>
      {showVolumeTypeModal && (
        <AddNewVolumeType
          onClose={() => setShowVolumeTypeModal(false)}
          volumeBackendName={volumeBackend.type}
          onSubmit={saveVolumeTypes}
        />
      )}
    </>
  )
}
